import type { ProductImageSlot } from './shared/productImageSlots';
import { deriveSlotView } from './shared/productImageSlots';

/**
 * Amazon 商品詳細ページのアダプタ (純関数)。
 * GET /api/amazon/products/<asin>/images のスロットと画面内の処理中状態から、
 * 共通コンポーネント用の枠モデル (ProductImageSlot) を組み立てる。
 * 計画: docs/requirements/product-detail-page-consolidation-plan.md 4.3
 */

/** 並べ替えの対象になる枠 (メイン画像は Amazon では特別扱いのため固定) */
export const AMAZON_SUB_SLOT_KEYS = Array.from({ length: 8 }, (_, i) => `pt${String(i + 1).padStart(2, '0')}`);

/** GET の slots を画面用に整えたもの (ページの state) */
export interface AmazonImageSlot {
  slot: string; // 'main', 'pt01', 'pt02', ...
  url: string | null;
  filled: boolean;
  label: string;
  /** 差し替え送信済みで Amazon のカタログにまだ反映されていないとき、送信した画像 (BE の slots[].pending) */
  pending?: { url: string; uploadedAt: string };
}

export function buildAmazonSlots(args: {
  imageSlots: AmazonImageSlot[];
  /** 差替処理中: スロット → 送信中の画像 URL */
  pendingReplacements: Map<string, string>;
  /** 削除処理中のスロット */
  pendingDeletions: Set<string>;
  /** スロット → 画像の名前 */
  imageNames: Record<string, string>;
  /** メイン画像の解像度 (GET の slots.main.width / height) */
  mainResolution?: { width: number; height: number } | null;
}): ProductImageSlot[] {
  const { imageSlots, pendingReplacements, pendingDeletions, imageNames, mainResolution } = args;
  return imageSlots.map((s) => {
    const isMain = s.slot === 'main';
    const replacingUrl = pendingReplacements.get(s.slot);
    const localOp: ProductImageSlot['localOp'] = pendingDeletions.has(s.slot)
      ? { kind: 'deleting' }
      : replacingUrl !== undefined
        ? { kind: 'replacing', url: replacingUrl }
        : null;
    return {
      key: s.slot,
      label: s.label,
      isMain,
      url: s.url,
      name: imageNames[s.slot] || null,
      pending: s.pending ?? null,
      localOp,
      resolution: isMain && mainResolution?.width && mainResolution?.height ? mainResolution : null,
      locked: isMain,
    };
  });
}

/**
 * Amazon アカウント未接続のとき: GET /images (Seller 連携が要る) を呼べないので、DB に保存済みの
 * カタログ画像 (main_image_url / additional_images[].variant) から同じ枠モデルを組み立てる。
 * 閲覧専用 (差替・削除・並べ替えなし) で、画像クリックの選択・フォルダ保存・名前編集だけできる。
 * variant (PT01..PT08) をスロットキーに写し、variant の無い画像は空いている枠に順に入れる。
 */
export function buildAmazonCatalogSlots(args: {
  mainImageUrl?: string | null;
  additionalImages?: Array<{ url: string; variant?: string | null }> | null;
  imageNames: Record<string, string>;
}): ProductImageSlot[] {
  const { mainImageUrl, additionalImages, imageNames } = args;
  const bySlot = new Map<string, string>();
  const unplaced: string[] = [];
  for (const img of additionalImages ?? []) {
    const url = (img?.url || '').trim();
    if (!url) continue;
    const key = (img.variant || '').trim().toLowerCase();
    if (AMAZON_SUB_SLOT_KEYS.includes(key) && !bySlot.has(key)) {
      bySlot.set(key, url);
    } else {
      unplaced.push(url);
    }
  }
  for (const key of AMAZON_SUB_SLOT_KEYS) {
    if (unplaced.length === 0) break;
    if (!bySlot.has(key)) bySlot.set(key, unplaced.shift()!);
  }
  const main = (mainImageUrl || '').trim() || null;
  return [
    {
      key: 'main', label: 'メイン画像', isMain: true, url: main,
      name: main ? imageNames.main || null : null, pending: null, localOp: null, resolution: null, locked: true,
    },
    ...AMAZON_SUB_SLOT_KEYS.map((key, i) => {
      const url = bySlot.get(key) ?? null;
      return {
        key, label: `画像 ${i + 1}`, isMain: false, url,
        name: url ? imageNames[key] || null : null, pending: null, localOp: null, resolution: null, locked: false,
      };
    }),
  ];
}

/**
 * 並べ替えを開始できない理由 (null = 開始できる)。現行の canStartReorder と同じ条件:
 * 画像1〜8のいずれかが反映待ち・処理中、ページ全体の送信中 (メイン画像の差替・削除を含む)、画像なし。
 * カタログのみ (SKU なし) はボタン自体を出さないので、ここでは扱わない (capabilities.reorder=false)。
 */
export function amazonReorderDisabledReason(args: {
  subSlots: ProductImageSlot[];
  /** uploadImageMutation.isPending (差替・削除の送信中。メイン画像を含む) */
  isUploading: boolean;
}): string | null {
  const { subSlots, isUploading } = args;
  const views = subSlots.map(deriveSlotView);
  if (subSlots.some((slot) => !!slot.pending)) return 'Amazon反映待ちの画像がある間は並べ替えできません';
  if (views.some((v) => v.busy) || isUploading) return '画像を送信中は並べ替えできません';
  if (!subSlots.some((slot) => !!slot.url)) return '並べ替えできる画像がありません';
  return null;
}
